import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";

// Lightweight client beacon. Batches events in memory and flushes via
// sendBeacon (or fetch keepalive) so app close / background doesn't lose
// the tail. Server endpoint accepts up to 25 events per POST.
//
// Tracked kinds:
//   app_open  — first load OR foreground after >5 min background
//   app_resume — foreground after <5 min background (separate metric)
//   page_view — every wouter route change
//
// Anonymous (no user_id) events are accepted by the server for pre-login
// pageviews; user_id is inferred from the session cookie on the server.

const ENDPOINT = "/api/analytics/events";
const FLUSH_DELAY_MS = 4_000;
const MAX_BATCH = 25;
const RESUME_THRESHOLD_MS = 5 * 60 * 1000;
const SESSION_KEY = "stoop:analyticsSession";

// Known kinds plus a generic escape so callers can log funnel/interaction
// events (e.g. "list_opened", "list_published") without widening this union.
type EventKind =
  | "app_open"
  | "app_resume"
  | "page_view"
  | "page_leave"
  | "error"
  | "tap_event"
  | (string & {});
type EventMeta = Record<string, unknown>;
type QueuedEvent = { kind: EventKind; path?: string; metadata?: EventMeta };

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let lastBackgroundAt: number | null = null;
let initialized = false;

// Per-page engagement tracking (dwell + max scroll depth).
let pageEnteredAt = 0;
let pagePath = "";
let maxScrollPct = 0;

function getSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return `s_${Date.now().toString(36)}`;
  }
}

function platformLabel(): string {
  if (typeof window === "undefined") return "ssr";
  if (Capacitor.isNativePlatform()) return Capacitor.getPlatform();
  return "web";
}

function buildPayload(events: QueuedEvent[]) {
  return {
    sessionId: getSessionId(),
    platform: platformLabel(),
    events: events.slice(0, MAX_BATCH),
  };
}

function currentScrollPct(): number {
  if (typeof window === "undefined") return 0;
  const shell = document.getElementById("app-scroll");
  const doc = document.documentElement;
  const viewport = shell ? shell.clientHeight : window.innerHeight;
  const full = shell
    ? shell.scrollHeight
    : Math.max(doc.scrollHeight, document.body.scrollHeight);
  if (full <= viewport) return 100; // nothing to scroll = fully seen
  const reached = (shell ? shell.scrollTop : window.scrollY) + viewport;
  return Math.max(0, Math.min(100, Math.round((reached / full) * 100)));
}

function flushNow(useBeacon = false) {
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const body = JSON.stringify(buildPayload(batch));
  try {
    if (useBeacon && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
  } catch {
    /* fall through to fetch */
  }
  void fetch(ENDPOINT, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushNow();
  }, FLUSH_DELAY_MS);
}

// Durable search logging (server writes to search_queries, not the analytics
// firehose). Returns the row id so a later result-tap can be attributed to the
// query that produced it. Fire-and-forget: logging must never break searching.
export async function logSearch(payload: {
  q: string;
  itemCount: number;
  producerCount: number;
  boxCount: number;
  assistUsed: boolean;
  assistTerms?: unknown;
}): Promise<string | null> {
  if (typeof window === "undefined") return null;
  try {
    const res = await fetch("/api/search/log", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ ...payload, sessionId: getSessionId(), platform: platformLabel() }),
    });
    if (!res.ok) return null;
    const data = await res.json().catch(() => null);
    return data?.id ?? null;
  } catch {
    return null;
  }
}

export function logSearchClick(id: string, target: { itemId?: string; producerId?: string }): void {
  if (typeof window === "undefined" || !id) return;
  void fetch("/api/search/click", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    keepalive: true,
    body: JSON.stringify({ id, ...target }),
  }).catch(() => {});
}

export function trackEvent(kind: EventKind, path?: string, metadata?: EventMeta) {
  if (typeof window === "undefined") return;
  queue.push({ kind, path, metadata });
  if (queue.length >= MAX_BATCH) {
    flushNow();
  } else {
    scheduleFlush();
  }
}

// Cart adds are a high-intent, low-frequency signal we don't want to lose to
// the 4s batch window or an app-kill. Send immediately to a dedicated endpoint
// that writes one durable row, with one retry on network failure. Fire-and-
// forget from the UI — never blocks the (synchronous) cart state update.
const CART_ADD_ENDPOINT = "/api/analytics/cart-add";

export function trackCartAdd(
  kind: "cart_add" | "express_cart_add",
  path: string | undefined,
  metadata: EventMeta,
) {
  if (typeof window === "undefined") return;
  const body = JSON.stringify({
    sessionId: getSessionId(),
    platform: platformLabel(),
    kind,
    path,
    metadata,
  });
  const send = () =>
    fetch(CART_ADD_ENDPOINT, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).then((r) => {
      if (!r.ok) throw new Error(`cart-add ${r.status}`);
    });
  send().catch(() => {
    setTimeout(() => {
      void send().catch(() => {});
    }, 2000);
  });
}

// Emit a page_leave event with how long the user spent and how far they
// scrolled on the page they're leaving. Called on route change, background,
// and unload. No-op if no page is being tracked.
function flushPageEngagement() {
  if (!pageEnteredAt || !pagePath) return;
  const dwellMs = Date.now() - pageEnteredAt;
  // Ignore instantaneous transitions (double route fires).
  if (dwellMs >= 300) {
    trackEvent("page_leave", pagePath, {
      dwellMs,
      scrollPct: Math.max(maxScrollPct, currentScrollPct()),
    });
  }
  pageEnteredAt = 0;
  pagePath = "";
  maxScrollPct = 0;
}

function currentPath(): string {
  if (typeof window === "undefined") return "/";
  return window.location.pathname + (window.location.search || "");
}

export function initAnalytics() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  trackEvent("app_open", currentPath());
  pageEnteredAt = Date.now();
  pagePath = currentPath();
  maxScrollPct = currentScrollPct();

  // Capture uncaught errors and promise rejections as analytics so we can see
  // where real users hit failures, not just what they bother to report.
  window.addEventListener("error", (e) => {
    trackEvent("error", currentPath(), {
      message: String(e.message || "").slice(0, 300),
      source: typeof e.filename === "string" ? e.filename.slice(0, 200) : undefined,
      line: e.lineno,
      col: e.colno,
      // The stack is what makes an error debuggable — message + minified line
      // alone is a dead end. Truncated to keep the beacon payload small.
      stack: typeof e.error?.stack === "string" ? e.error.stack.slice(0, 1500) : undefined,
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason: any = (e as PromiseRejectionEvent).reason;
    trackEvent("error", currentPath(), {
      message: String(reason?.message || reason || "").slice(0, 300),
      via: "rejection",
      stack: typeof reason?.stack === "string" ? reason.stack.slice(0, 1500) : undefined,
    });
  });

  // Track the deepest scroll the user reaches on each page (passive listener).
  window.addEventListener(
    "scroll",
    () => {
      const pct = currentScrollPct();
      if (pct > maxScrollPct) maxScrollPct = pct;
    },
    { passive: true },
  );

  // Web visibility change: fires on tab focus/blur. Used to detect resume.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      lastBackgroundAt = Date.now();
      flushPageEngagement();
      flushNow(true);
    } else if (document.visibilityState === "visible" && lastBackgroundAt) {
      const away = Date.now() - lastBackgroundAt;
      lastBackgroundAt = null;
      trackEvent(away > RESUME_THRESHOLD_MS ? "app_open" : "app_resume", currentPath());
      pageEnteredAt = Date.now();
      pagePath = currentPath();
      maxScrollPct = currentScrollPct();
    }
  });

  // Native app state changes (Capacitor). Mirrors visibility for the wrapped app.
  if (Capacitor.isNativePlatform()) {
    void CapApp.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) {
        lastBackgroundAt = Date.now();
        flushNow(true);
      } else if (lastBackgroundAt) {
        const away = Date.now() - lastBackgroundAt;
        lastBackgroundAt = null;
        trackEvent(away > RESUME_THRESHOLD_MS ? "app_open" : "app_resume", currentPath());
      }
    });
  }

  // Best-effort flush on full page unload (web). Capacitor doesn't fire this
  // for in-app navigation but it covers external nav / link-out cases.
  window.addEventListener("pagehide", () => {
    flushPageEngagement();
    flushNow(true);
  });
  window.addEventListener("beforeunload", () => {
    flushPageEngagement();
    flushNow(true);
  });
}

export function trackPageView(path: string) {
  // Close out engagement on the page being left, then start the new one.
  flushPageEngagement();
  trackEvent("page_view", path);
  pageEnteredAt = Date.now();
  pagePath = path;
  maxScrollPct = 0;
}
